import { carrierIcons } from "../../assets/icons";
import {
  HiOutlineArrowRight,
  HiOutlineClock,
  HiOutlineCurrencyDollar,
} from "react-icons/hi2";
import { useRef } from "react";

export function RateRow({ rate, onSelect, selectedRate, isCheapest }) {
  const boxRef = useRef();
  const isSelected = selectedRate?.serviceId === rate?.serviceId;

  function getNextBusinessDay(date) {
    const d = new Date(date);
    const day = d.getDay();

    if (day === 6) {
      // Saturday → Monday
      d.setDate(d.getDate() + 2);
    } else if (day === 0) {
      // Sunday → Monday
      d.setDate(d.getDate() + 1);
    }

    return d;
  }

  // Add business days AFTER pickup
  function addBusinessDays(startDate, businessDays) {
    const result = new Date(startDate);
    let addedDays = 0;

    while (addedDays < businessDays) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();

      if (day !== 0 && day !== 6) {
        addedDays++;
      }
    }

    return result;
  }

  const pickupDate = getNextBusinessDay(new Date());
  const expectedArrivalDate = addBusinessDays(
    pickupDate,
    Number(rate.transitDays)
  );
  const formattedDate = expectedArrivalDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div
      ref={boxRef}
      className={`relative grid grid-cols-12 gap-10 items-center px-6 py-5 border-b border-gray-100 transition-all duration-200
        ${
          isSelected
            ? "bg-blue-50/60 shadow-md ring-0 ring-blue-200 z-10 ring-l-none"
            : "bg-white hover:bg-gray-50"
        }
      `}
    >
      {/* Selected Accent Bar */}
      {isSelected && (
        <span className="absolute left-[-5px] top-0 h-full w-1 bg-blue-500 rounded-l-full z-10" />
      )}

      {/* Carrier & Service */}
      <div className="col-span-3 flex items-center gap-3 min-w-0">
        <div
          className={`flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-lg transition
            ${isSelected ? "" : ""}
          `}
        >
          {carrierIcons[rate.carrierName]}
        </div>

        <div className="min-w-0 flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {rate.carrierName}
          </span>

          <span
            className={`text-xs px-2 py-0.5 rounded-full w-max truncate transition
              ${
                isSelected
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-600"
              }
            `}
          >
            {rate.serviceName}
          </span>
        </div>
      </div>

      {/* Transit Days + Expected Arrival */}
      <div className="col-span-2 flex flex-col gap-1 text-sm">
        <div className="flex items-center gap-2 text-gray-500">
          <HiOutlineClock className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span>{rate.transitDays} day(s)</span>
        </div>
        <span className="text-xs text-gray-400">Arrives: {formattedDate}</span>
      </div>

      {/* Delivery Type */}
      <div className="col-span-3 flex flex-wrap items-start">
        <span className="text-xs bg-cyan-100 text-cyan-800 px-2 py-0.5 rounded-full font-medium">
          Drop-off
        </span>
      </div>

      {/* Price + Tax + Cheapest */}
      <div className="col-span-3 flex flex-col items-start gap-1">
        <div className="flex items-center gap-2">
          <HiOutlineCurrencyDollar className="w-4 h-4 text-gray-400" />
          <span
            className={`text-sm font-semibold transition
              ${isSelected ? "text-blue-700" : "text-gray-900"}
            `}
          >
            {rate.totalCharge} {rate.currency}
          </span>
        </div>

        {rate.taxCharge > 0 && (
          <span className="text-xs text-gray-500">
            Includes {Number(rate.taxCharge).toFixed(2)} {rate.currency} taxes
          </span>
        )}

        {isCheapest && (
          <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
            Cheapest
          </span>
        )}
      </div>

      {/* Action */}
      <div className="col-span-1 flex justify-end">
        <button
          onClick={() => onSelect(rate)}
          aria-label={`Select ${rate.carrierName} ${rate.serviceName}`}
          className={`p-3 rounded-full transition-all flex items-center justify-center
            ${
              isSelected
                ? "bg-blue-500 text-white shadow-sm"
                : "bg-slate-100 hover:bg-slate-200 text-[#0052FF]"
            }
          `}
        >
          <HiOutlineArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
